/**
 * GameSettingsScreen - Page des paramètres de jeu
 * Mode lecture, mode sombre, quitter la partie
 * DESIGN SYSTEM APPLIQUÉ
 */

import React, {useState, useMemo} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Pressable,
  SafeAreaView,
  Platform,
  Switch,
  Modal,
  ImageBackground,
} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faArrowLeft, faCog, faMoon, faSun, faBook, faBookOpen, faDoorOpen} from '@fortawesome/free-solid-svg-icons';
import {Fonts} from '../config/fonts';
import {usePlayer} from '../contexts/PlayerContext';
import {useTheme} from '../contexts/ThemeContext';
import {Theme} from '../theme/theme';

// Import des textures de papier
import paperLight from '../assets/img/paper_light.png';
import paperDark from '../assets/img/paper_dark.png';

const GameSettingsScreen: React.FC = () => {
  const navigation = useNavigation();
  const {resetGameState} = usePlayer();
  const {theme, isDarkMode, isReadingMode, toggleTheme, toggleReadingMode} = useTheme();
  
  const [showQuitConfirmation, setShowQuitConfirmation] = useState(false);
  
  // Générer les styles basés sur le thème
  const styles = useMemo(() => createStyles(theme, isReadingMode), [theme, isReadingMode]);

  const handleQuitGame = () => {
    console.log('[GameSettingsScreen] handleQuitGame called');
    // Ouvrir la modale de confirmation
    setShowQuitConfirmation(true);
  };

  const confirmQuitGame = () => {
    console.log('[GameSettingsScreen] Quit confirmed');
    
    // Réinitialiser le gameState
    resetGameState();
    // Fermer la modale
    setShowQuitConfirmation(false);
    
    // En mode édition (popup), fermer la fenêtre du navigateur
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.opener) {
      console.log('[GameSettingsScreen] In popup/editing mode, closing window');
      window.close();
      return;
    }
    
    // Mode normal : naviguer vers le menu principal
    // La progression est déjà sauvegardée automatiquement
    console.log('[GameSettingsScreen] Navigating to Main');
    try {
      navigation.navigate('Main' as never);
    } catch (error) {
      console.error('[GameSettingsScreen] Error navigating to Main:', error);
      // Fallback: essayer goBack si navigate échoue
      if (navigation.canGoBack()) {
        navigation.goBack();
      }
    }
  };

  const cancelQuitGame = () => {
    setShowQuitConfirmation(false);
  };

  return (
    <View style={[styles.container, isReadingMode && styles.containerReadingMode]}>
      {/* Texture papier : masquée en mode lecture (liseuse) */}
      {!isReadingMode && (
        <ImageBackground
          source={isDarkMode ? paperDark : paperLight}
          style={StyleSheet.absoluteFill}
          imageStyle={styles.backgroundImage}
        />
      )}
      <SafeAreaView style={styles.safeArea}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}>
            <FontAwesomeIcon icon={faArrowLeft} size={20} color={theme.colors.text} />
          </TouchableOpacity>
          <FontAwesomeIcon icon={faCog} size={24} color={theme.colors.accent} />
          <Text style={styles.headerTitle}>Paramètres de jeu</Text>
          <View style={styles.headerSpacer} />
        </View>

        {/* Content */}
        <ScrollView 
          style={styles.scrollView} 
          contentContainerStyle={styles.content}
          nestedScrollEnabled={false}
          keyboardShouldPersistTaps="handled">
        {/* Section Affichage */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Affichage</Text>
          
          {/* Mode sombre */}
          <View style={styles.settingItem}>
            <View style={styles.settingLeft}>
              <View style={styles.settingIcon}>
                <FontAwesomeIcon 
                  icon={isDarkMode ? faMoon : faSun} 
                  size={18} 
                  color={theme.colors.accent}
                />
              </View>
              <View style={styles.settingContent}>
                <Text style={styles.settingLabel}>Mode sombre</Text>
                <Text style={styles.settingDescription}>
                  Interface avec fond sombre pour une meilleure lisibilité
                </Text>
              </View>
            </View>
            <Switch
              value={isDarkMode}
              onValueChange={toggleTheme}
              trackColor={{
                false: theme.colors.cardBg,
                true: theme.colors.toggleActive
              }}
              thumbColor="#FDFBF8"
              style={!isDarkMode && styles.switchOff}
            />
          </View>

          {/* Mode lecture */}
          <View style={styles.settingItem}>
            <View style={styles.settingLeft}>
              <View style={styles.settingIcon}>
                <FontAwesomeIcon 
                  icon={isReadingMode ? faBookOpen : faBook} 
                  size={18} 
                  color={theme.colors.accent}
                />
              </View>
              <View style={styles.settingContent}>
                <Text style={styles.settingLabel}>Mode lecture</Text>
                <Text style={styles.settingDescription}>
                  Améliore la lisibilité du texte narratif
                </Text>
              </View>
            </View>
            <Switch
              value={isReadingMode}
              onValueChange={toggleReadingMode}
              trackColor={{
                false: theme.colors.cardBg,
                true: theme.colors.toggleActive
              }}
              thumbColor="#FDFBF8"
              style={!isReadingMode && styles.switchOff}
            />
          </View>
        </View>

        {/* Divider */}
        <View style={styles.divider} />

        {/* Section Actions */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Actions</Text>
          
          {/* Quitter la partie */}
          <Pressable
            style={({pressed}) => [
              styles.actionButton,
              pressed && styles.actionButtonPressed,
            ]}
            onPress={handleQuitGame}
            accessible={true}
            accessibilityLabel="Quitter la partie"
            accessibilityRole="button">
            <View style={styles.settingIcon}>
              <FontAwesomeIcon 
                icon={faDoorOpen} 
                size={18} 
                color={theme.colors.accent}
                pointerEvents="none"
              />
            </View>
            <View style={styles.settingContent} pointerEvents="none">
              <Text style={styles.actionLabel}>Quitter la partie</Text>
              <Text style={styles.settingDescription}>
                Retourner au menu principal
              </Text>
            </View>
          </Pressable>
        </View>
      </ScrollView>

      {/* Delete Confirmation Modal */}
      <Modal
        transparent
        visible={showQuitConfirmation}
        animationType="fade"
        onRequestClose={cancelQuitGame}>
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={cancelQuitGame}>
          <View
            style={styles.modalContainer}
            onStartShouldSetResponder={() => true} // Prevent closing when clicking inside the modal
          >
            <Text style={styles.modalTitle}>Quitter la partie ?</Text>
            <Text style={styles.modalMessage}>Votre progression sera sauvegardée automatiquement.</Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonCancel]}
                onPress={cancelQuitGame}>
                <Text style={styles.modalButtonTextCancel}>Annuler</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonConfirm]}
                onPress={confirmQuitGame}>
                <Text style={styles.modalButtonTextConfirm}>Quitter</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
      </SafeAreaView>
    </View>
  );
};

// Fonction pour créer les styles basés sur le thème ET mode lecture
const createStyles = (theme: Theme, isReadingMode: boolean) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  containerReadingMode: {
    backgroundColor: isReadingMode 
      ? (theme.colors.bg === '#EFE8DF' || theme.colors.bg === '#FBF8F2' ? '#FFFEF9' : '#1C1A17')
      : theme.colors.bg,
  },
  backgroundImage: {
    opacity: 1,
    width: '100%',
    height: '100%',
  },
  safeArea: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.borderLight,
    ...theme.elevation.header,
  },
  backButton: {
    padding: theme.spacing.sm,
    marginRight: theme.spacing.sm,
  },
  headerTitle: {
    flex: 1,
    fontSize: theme.fontSize.stepTitle,
    fontWeight: '600',
    color: theme.colors.text,
    marginLeft: theme.spacing.md,
    fontFamily: Fonts.title,
  },
  headerSpacer: {
    width: 36,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xxl,
  },
  section: {
    marginBottom: theme.spacing.xxl,
  },
  sectionTitle: {
    fontSize: theme.fontSize.ui + 4,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: theme.spacing.lg,
    fontFamily: Fonts.title,
    letterSpacing: 0.5,
  },
  settingItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: theme.cardPremium.paddingVertical,
    paddingHorizontal: theme.cardPremium.paddingHorizontal,
    backgroundColor: isReadingMode ? 'transparent' : theme.colors.cardBg,
    borderRadius: isReadingMode ? theme.radius.lg : theme.cardPremium.borderRadius,
    borderWidth: isReadingMode ? 0 : theme.cardPremium.borderWidth,
    borderColor: isReadingMode ? 'transparent' : theme.colors.cardBorder,
    borderTopWidth: isReadingMode ? 0 : 2,
    borderTopColor: isReadingMode ? 'transparent' : theme.colors.cardHighlight,
    borderBottomWidth: isReadingMode ? 1 : 0,
    borderBottomColor: isReadingMode ? theme.colors.borderLight : 'transparent',
    marginBottom: theme.spacing.md,
    ...Platform.select({
      web: {
        boxShadow: isReadingMode ? 'none' : `0 2px 8px rgba(${theme.colors.cardShadow === '#000' ? '0,0,0' : '139,115,85'}, 0.08)`,
        backgroundImage: isReadingMode ? 'none' : `linear-gradient(to bottom, ${theme.colors.cardBgTop}, ${theme.colors.cardBgBottom})`,
      },
      default: {
        shadowColor: 'transparent',
        shadowOffset: {width: 0, height: 0},
        shadowOpacity: 0,
        shadowRadius: 0,
        elevation: 0,
      },
    }),
  },
  settingLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: theme.spacing.lg,
  },
  settingIcon: {
    width: isReadingMode ? 0 : theme.cardPremium.iconBadgeSize,
    height: isReadingMode ? 0 : theme.cardPremium.iconBadgeSize,
    borderRadius: theme.cardPremium.iconBadgeRadius,
    backgroundColor: isReadingMode ? 'transparent' : theme.colors.objectBg,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: isReadingMode ? 0 : theme.spacing.md,
    opacity: isReadingMode ? 0 : 1,
    overflow: 'hidden',
  },
  settingContent: {
    flex: 1,
  },
  settingLabel: {
    fontSize: theme.fontSize.ui,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 4,
    fontFamily: Fonts.ui,
    letterSpacing: 0.2,
  },
  settingDescription: {
    fontSize: 13,
    color: theme.colors.textMuted,
    fontFamily: Fonts.reading,
    lineHeight: 19,
    letterSpacing: 0.1,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.borderLight,
    marginVertical: theme.spacing.sm,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.cardPremium.paddingVertical,
    paddingHorizontal: theme.cardPremium.paddingHorizontal,
    backgroundColor: isReadingMode ? 'transparent' : theme.colors.cardBg,
    borderRadius: isReadingMode ? theme.radius.lg : theme.cardPremium.borderRadius,
    borderWidth: isReadingMode ? 0 : theme.cardPremium.borderWidth,
    borderColor: isReadingMode ? 'transparent' : theme.colors.cardBorder,
    borderTopWidth: isReadingMode ? 0 : 2,
    borderTopColor: isReadingMode ? 'transparent' : theme.colors.cardHighlight,
    borderBottomWidth: isReadingMode ? 1 : 0,
    borderBottomColor: isReadingMode ? theme.colors.borderLight : 'transparent',
    marginBottom: theme.spacing.md,
    opacity: isReadingMode ? 1 : 0.85,
    ...Platform.select({
      web: {
        boxShadow: isReadingMode ? 'none' : `0 2px 8px rgba(${theme.colors.cardShadow === '#000' ? '0,0,0' : '139,115,85'}, 0.06)`,
        backgroundImage: isReadingMode ? 'none' : `linear-gradient(to bottom, ${theme.colors.cardBgTop}, ${theme.colors.cardBgBottom})`,
      },
      default: {
        shadowColor: 'transparent',
        shadowOffset: {width: 0, height: 0},
        shadowOpacity: 0,
        shadowRadius: 0,
        elevation: 0,
      },
    }),
  },
  actionButtonPressed: {
    opacity: 0.7,
    transform: [{scale: 0.98}],
  },
  actionLabel: {
    fontSize: theme.fontSize.ui,
    fontWeight: '600',
    color: theme.colors.text, // Plus discret, pas accent
    marginBottom: 4,
    fontFamily: Fonts.ui,
    opacity: 0.85,
  },
  switchOff: {
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px rgba(139, 115, 85, 0.15)',
      },
      default: {
        shadowColor: theme.colors.cardShadow,
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.15,
        shadowRadius: 3,
        elevation: 1,
      },
    }),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.radius.xl,
    padding: theme.spacing.xl,
    width: '80%',
    maxWidth: 400,
    alignItems: 'center',
    ...theme.elevation.modal,
  },
  modalTitle: {
    fontSize: theme.fontSize.ui + 4,
    fontWeight: 'bold',
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    fontFamily: Fonts.title,
    textAlign: 'center',
  },
  modalMessage: {
    fontSize: theme.fontSize.ui - 1,
    color: theme.colors.textMuted,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
    fontFamily: Fonts.reading,
    lineHeight: 22,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  modalButton: {
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.radius.md,
    minWidth: 120,
    alignItems: 'center',
  },
  modalButtonCancel: {
    backgroundColor: theme.colors.surface2,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  modalButtonConfirm: {
    backgroundColor: theme.colors.surface2, // Même style que annuler
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  modalButtonTextCancel: {
    color: theme.colors.text,
    fontWeight: '600',
    fontFamily: Fonts.reading,
  },
  modalButtonTextConfirm: {
    color: theme.colors.text, // Même couleur que annuler
    fontWeight: '600',
    fontFamily: Fonts.reading,
  },
});

export default GameSettingsScreen;
